import React from 'react';
import {
    Box,
    TextField,
    Typography,
    Button,
    Paper,
    IconButton,
    Chip,
    Alert
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    DragIndicator as DragIcon,
    ArrowUpward as UpIcon,
    ArrowDownward as DownIcon
} from '@mui/icons-material';
import RichTextInput from './RichTextInput';

interface SortingEditorProps {
    content: any;
    onChange: (content: any) => void;
}

interface SortableItem {
    id: string;
    text: string;
    position: number; // Correct position (1-indexed)
}

const SortingEditor: React.FC<SortingEditorProps> = ({ content, onChange }) => {
    const instruction = content?.instruction || '';
    const learningContent = content?.learningContent || '';
    const items: SortableItem[] = content?.items || [];

    const addItem = () => {
        const newItem: SortableItem = {
            id: Date.now().toString(),
            text: '',
            position: items.length + 1
        };
        onChange({ ...content, items: [...items, newItem] });
    };

    const updateItem = (itemId: string, field: string, value: any) => {
        const updatedItems = items.map(item =>
            item.id === itemId ? { ...item, [field]: value } : item
        );
        onChange({ ...content, items: updatedItems });
    };

    const deleteItem = (itemId: string) => {
        const updatedItems = items
            .filter(item => item.id !== itemId)
            .map((item, index) => ({ ...item, position: index + 1 }));
        onChange({ ...content, items: updatedItems });
    };

    const moveItem = (index: number, direction: 'up' | 'down') => {
        if (
            (direction === 'up' && index === 0) ||
            (direction === 'down' && index === items.length - 1)
        ) {
            return;
        }

        const newItems = [...items];
        const swapIndex = direction === 'up' ? index - 1 : index + 1;

        // Swap items
        [newItems[index], newItems[swapIndex]] = [newItems[swapIndex], newItems[index]];

        // Update positions
        const updatedItems = newItems.map((item, idx) => ({
            ...item,
            position: idx + 1
        }));

        onChange({ ...content, items: updatedItems });
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Sortieraufgabe (Ordering)</Typography>
            </Box>

            <Alert severity="info">
                Lernende bringen Elemente in die richtige Reihenfolge. Die Reihenfolge, die Sie hier festlegen, ist die korrekte Lösung.
            </Alert>

            {/* Learning content (optional) */}
            <RichTextInput
                label="Lerninhalt (optional)"
                value={learningContent}
                onChange={(val) => onChange({ ...content, learningContent: val })}
                helperText="Dieser formatierte Text wird im Player über dem Quiz angezeigt."
                placeholder="Optionaler Lernstoff, der über dem Quiz angezeigt wird..."
            />

            {/* Instruction */}
            <TextField
                label="Aufgabenstellung"
                fullWidth
                multiline
                rows={3}
                value={instruction}
                onChange={(e) => onChange({ ...content, instruction: e.target.value })}
                placeholder="Bringen Sie die Schritte in die richtige Reihenfolge..."
            />

            {/* Items */}
            <Paper sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1">Elemente (in korrekter Reihenfolge)</Typography>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={addItem}>
                        Element hinzufügen
                    </Button>
                </Box>

                {items.length === 0 ? (
                    <Typography color="text.secondary" textAlign="center">
                        Noch keine Elemente definiert
                    </Typography>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {items.map((item, index) => (
                            <Paper key={item.id} sx={{ p: 2, bgcolor: 'background.default' }}>
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                    <DragIcon sx={{ color: 'text.secondary' }} />
                                    <Chip label={`#${item.position}`} color="primary" size="small" />

                                    <TextField
                                        label="Text"
                                        size="small"
                                        fullWidth
                                        required
                                        value={item.text}
                                        onChange={(e) => updateItem(item.id, 'text', e.target.value)}
                                        placeholder="z.B. Schritt 1: Vorbereitung"
                                        sx={{ flex: 1 }}
                                    />

                                    {/* Move Up/Down */}
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <IconButton
                                            onClick={() => moveItem(index, 'up')}
                                            disabled={index === 0}
                                            size="small"
                                        >
                                            <UpIcon />
                                        </IconButton>
                                        <IconButton
                                            onClick={() => moveItem(index, 'down')}
                                            disabled={index === items.length - 1}
                                            size="small"
                                        >
                                            <DownIcon />
                                        </IconButton>
                                    </Box>

                                    <IconButton
                                        onClick={() => deleteItem(item.id)}
                                        color="error"
                                        size="small"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            </Paper>
                        ))}
                    </Box>
                )}
            </Paper>

            {/* Summary */}
            {items.length > 0 && (
                <Alert severity="success">
                    {items.length} Element(e) definiert - Die aktuelle Reihenfolge ist die korrekte Lösung
                </Alert>
            )}
        </Box>
    );
};

export default SortingEditor;
